import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { ConfirmationNumber } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { createFilterOptions, Util } from '../../util/Util';
import RefreshButton from '../../components/RefreshButton';
import {
  MoleDialog,
  MoleDialogHeader,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import { excludeExpensePayModes } from '../../util/constants';
import { MONEY_LIMITS, parseMoneyInput } from '../../util/quantity';
import type {
  VoucherPayModeOption,
  VoucherPosOption,
  VoucherPreset,
  VoucherRow,
} from './voucherTypes';
import { MAX_VOUCHER_QUANTITY, SYSTEM_VOUCHER_TYPES, VOUCHER_STATUSES, voucherStatusI18nKey } from './voucherTypes';
import { businessTodayDate } from "../../util/businessClock";

type Props = {
  isOpen: boolean;
  voucher?: VoucherRow | null;
  preset?: VoucherPreset | null;
  onClose: () => void;
  onSuccess: (id?: number) => void;
};

const parseIsoDate = (value?: string | null) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addExpiry = (from: Date, preset: string) => {
  const next = startOfDay(from);
  if (preset === 'days7') next.setDate(next.getDate() + 7);
  else if (preset === 'days30') next.setDate(next.getDate() + 30);
  else if (preset === 'days60') next.setDate(next.getDate() + 60);
  else if (preset === 'days90') next.setDate(next.getDate() + 90);
  else if (preset === 'months6') next.setMonth(next.getMonth() + 6);
  else if (preset === 'year1') next.setFullYear(next.getFullYear() + 1);
  else return null;
  return next;
};

const defaultExpiry = (from: Date = businessTodayDate()) => addExpiry(from, 'year1');

const EXPIRY_PRESETS = ['days7', 'days30', 'days60', 'days90', 'months6', 'year1', 'none'] as const;

const VoucherModal = ({ isOpen, voucher = null, preset = null, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { api, access } = useAuth();
  const { setLoadingMessage } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const shopName = String(appSettings?.BIZ_NAME_FULL?.value || appSettings?.BIZ_BRANCH?.value || '').trim();

  const [code, setCode] = useState('');
  const [voucherType, setVoucherType] = useState('gift_card');
  const [issuer, setIssuer] = useState('');
  const [issuers, setIssuers] = useState<string[]>([]);
  const [amount, setAmount] = useState(0);
  const [issued, setIssued] = useState<Date>(() => businessTodayDate());
  const [expiry, setExpiry] = useState<Date | null>(defaultExpiry());
  const [pos, setPos] = useState<VoucherPosOption | null>(null);
  const [posOptions, setPosOptions] = useState<VoucherPosOption[]>([]);
  const [mode, setMode] = useState<VoucherPayModeOption | null>(null);
  const [modeOptions, setModeOptions] = useState<VoucherPayModeOption[]>([]);
  const [issuee, setIssuee] = useState('');
  const [issuees, setIssuees] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [status, setStatus] = useState('ACTIVE');
  const [expiryPreset, setExpiryPreset] = useState<string>('year1');
  const [loadingLookups, setLoadingLookups] = useState(false);

  const typeLabel = (value: string) => {
    const translated = t(`common:voucher.types.${value}`);
    return translated.startsWith('common:') ? value : translated;
  };

  const allowChange = Boolean(
    voucher?.type === 'change'
    || preset?.allowChange
    || preset?.type === 'change',
  );

  const typeChoices = useMemo(
    () => SYSTEM_VOUCHER_TYPES.filter((item) => item !== 'change' || allowChange),
    [allowChange],
  );

  const typeLocked = Boolean(!voucher && (preset?.lockType || preset?.fromPos));
  const posLocked = Boolean(!voucher && (preset?.lockPos || preset?.fromPos));
  const quantityLocked = Boolean(
    voucher
    || voucherType === 'change'
    || voucherType === 'credit_note',
  );
  const lockedPos = preset?.pos
    ? { id: Number(preset.pos), pos_name: preset.pos_name || '' }
    : null;

  const applyExpiryPreset = (presetKey: string, from = issued) => {
    setExpiryPreset(presetKey);
    setExpiry(presetKey === 'none' ? null : addExpiry(from, presetKey));
  };

  const loadLookups = async () => {
    setLoadingLookups(true);
    const [issuerRes, issueeRes, posRes, modeRes] = await Promise.all([
      api.get('/vouchers/issuers'),
      api.get('/vouchers/issuees'),
      api.get('/vouchers/pos'),
      api.get('/payment/modes/active'),
    ]);
    if (issuerRes?.status === 200 && Array.isArray(issuerRes.data)) {
      setIssuers(issuerRes.data);
    }
    if (issueeRes?.status === 200 && Array.isArray(issueeRes.data)) {
      setIssuees(issueeRes.data);
    }
    if (posRes?.status === 200 && Array.isArray(posRes.data)) {
      const rows = posRes.data as VoucherPosOption[];
      const extra = lockedPos && !rows.some((row) => Number(row.id) === Number(lockedPos.id))
        ? [lockedPos]
        : [];
      const editExtra = voucher?.pos && !rows.some((row) => Number(row.id) === Number(voucher.pos))
        ? [{ id: Number(voucher.pos), pos_name: voucher.pos_name || '' }]
        : [];
      setPosOptions([...extra, ...editExtra, ...rows]);
      if (lockedPos) {
        setPos(lockedPos);
      } else if (voucher?.pos) {
        setPos(rows.find((row) => Number(row.id) === Number(voucher.pos)) || {
          id: voucher.pos,
          pos_name: voucher.pos_name || '',
        });
      }
    } else if (lockedPos) {
      setPos(lockedPos);
      setPosOptions([lockedPos]);
    }
    if (modeRes?.status === 200) {
      const nextModes = excludeExpensePayModes((modeRes.data || []) as VoucherPayModeOption[]);
      if (voucher?.mode && !nextModes.some((row) => Number(row.id) === Number(voucher.mode))) {
        nextModes.unshift({
          id: Number(voucher.mode),
          mode_name: voucher.mode_name || String(voucher.mode),
        });
      }
      setModeOptions(nextModes);
      if (voucher?.mode) {
        setMode(nextModes.find((row) => Number(row.id) === Number(voucher.mode)) || null);
      }
    }
    setLoadingLookups(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (voucher) {
      setCode(voucher.code || '');
      setVoucherType(voucher.type || 'gift_card');
      setIssuer(voucher.issuer || shopName);
      setAmount(Number(voucher.amount || 0));
      setIssued(parseIsoDate(voucher.issued) || new Date());
      setExpiry(parseIsoDate(voucher.expiry));
      setPos(voucher.pos ? { id: voucher.pos, pos_name: voucher.pos_name || '' } : null);
      setMode(voucher.mode ? { id: Number(voucher.mode), mode_name: voucher.mode_name || '' } : null);
      setIssuee(voucher.issuee || '');
      setDescription(voucher.description || '');
      setQuantity(1);
      setExpiryPreset(voucher.expiry ? 'custom' : 'none');
      setStatus(voucher.status || 'ACTIVE');
    } else {
      const nextIssued = new Date();
      setCode('');
      setVoucherType(preset?.type || 'gift_card');
      setIssuer(preset?.issuer || shopName);
      setAmount(Number(preset?.amount || 0));
      setIssued(nextIssued);
      setExpiry(defaultExpiry(nextIssued));
      setExpiryPreset('year1');
      setPos(lockedPos);
      setMode(null);
      setIssuee(preset?.issuee || '');
      setDescription(preset?.description || '');
      setQuantity(1);
      setStatus('ACTIVE');
    }
    void loadLookups();
  }, [isOpen, voucher, preset]);

  const expiryTooSoon = Boolean(expiry && startOfDay(expiry) < startOfDay(issued));
  const modeRequired = voucherType === 'change';
  const canSubmit = amount > 0
    && voucherType.trim().length > 0
    && issuer.trim().length > 0
    && !expiryTooSoon
    && (!modeRequired || Boolean(mode?.id));

  const handleSave = async () => {
    if (!canSubmit) return;
    if (!Util.checkDating(Util.formatISODate(issued), access, 'voucher')) return;
    const payload = {
      code: quantity > 1 ? null : (code.trim() || null),
      type: voucherType,
      issuer: issuer.trim(),
      amount,
      issued: Util.formatISODate(issued),
      expiry: expiry ? Util.formatISODate(expiry) : null,
      pos: (posLocked ? lockedPos?.id : pos?.id) ?? null,
      mode: mode?.id ?? null,
      issuee: issuee.trim() || null,
      description: description.trim() || null,
      batch: voucher
        ? (voucher.batch || null)
        : (voucherType === 'change' ? (preset?.batch || null) : null),
      invoice: voucher
        ? null
        : (voucherType === 'credit_note' ? (preset?.invoice || preset?.batch || null) : null),
      status,
      quantity: quantityLocked ? 1 : quantity,
    };
    setLoadingMessage(voucher ? 'UPDATE' : 'SAVE');
    const res = voucher
      ? await api.put(`/vouchers/${voucher.id}`, payload)
      : await api.post('/vouchers', payload);
    setLoadingMessage(null);
    if (res?.status === 201 || res?.status === 204) {
      toast.success(
        t(
          voucher
            ? 'common:toast.success.voucherUpdate'
            : voucherType === 'credit_note'
              ? 'common:toast.success.creditNote'
              : quantity > 1
                ? 'common:toast.success.voucherBatch'
                : 'common:toast.success.voucher',
        ),
        { autoClose: 3000 },
      );
      onSuccess(res?.data?.id);
      onClose();
    }
  };

  return (
    <MoleDialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      themeColor={themeColor}
      disableEnforceFocus
    >
      <MoleDialogHeader
        title={t(voucher ? 'common:voucher.editTitle' : 'common:voucher.postTitle')}
        subtitle={t('common:voucher.postHint')}
        icon={<ConfirmationNumber />}
        onClose={onClose}
        themeColor={themeColor}
      />
      <DialogContent>
        <Stack spacing={2.25} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <Autocomplete
              options={typeChoices}
              getOptionLabel={(value) => typeLabel(String(value))}
              value={voucherType}
              disabled={typeLocked}
              onChange={(_, value) => {
                if (!value) return;
                setVoucherType(value);
                if (value === 'change' || value === 'credit_note') {
                  setQuantity(1);
                }
              }}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField variant="filled" {...params} required label={t('common:voucher.type')} />
              )}
            />
            <TextField variant="filled"
              type="number"
              label={t('common:tables.amount')}
              value={Number.isFinite(amount) ? amount : 0}
              onFocus={(e) => e.target.select()}
              inputProps={{ min: 0.01, max: MONEY_LIMITS.max, step: 0.01 }}
              onChange={(e) => {
                const parsed = parseMoneyInput(e.target.value);
                if (parsed === null) return;
                setAmount(Math.max(0, Math.min(MONEY_LIMITS.max, parsed)));
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <Autocomplete
              freeSolo
              options={issuers}
              filterOptions={createFilterOptions((name) => name)}
              value={issuer}
              onInputChange={(_, value) => setIssuer(value)}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField variant="filled" {...params} required label={t('common:voucher.issuer')} helperText={t('common:voucher.issuerHint')} />
              )}
            />
            <Autocomplete
              freeSolo
              options={issuees}
              filterOptions={createFilterOptions((name) => name)}
              value={issuee}
              onInputChange={(_, value) => setIssuee(value)}
              loading={loadingLookups}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField variant="filled"
                  {...params}
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {t('common:voucher.issuee')}
                      <RefreshButton isWorking={loadingLookups} onClick={loadLookups} disabled={loadingLookups} />
                    </Box>
                  }
                  helperText={t('common:voucher.issueeHint')}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('common:voucher.issued')}
                value={issued}
                onChange={(next) => {
                  if (!next) return;
                  const nextDate = next instanceof Date ? next : next.toDate();
                  if (Number.isNaN(nextDate.getTime())) return;
                  const nextIssued = startOfDay(nextDate);
                  setIssued(nextIssued);
                  if (expiryPreset !== 'custom' && expiryPreset !== 'none') {
                    setExpiry(addExpiry(nextIssued, expiryPreset));
                  } else if (expiry && startOfDay(expiry) < nextIssued) {
                    setExpiry(nextIssued);
                  }
                }}
                slotProps={{
                  textField: { fullWidth: true, required: true, variant: "filled" },
                  popper: { sx: { zIndex: Z_INDEX.dialogPopover } },
                  dialog: { sx: { zIndex: Z_INDEX.dialogPopover } },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('common:voucher.expiry')}
                value={expiry}
                minDate={issued}
                onChange={(next) => {
                  if (!next) {
                    applyExpiryPreset('none');
                    return;
                  }
                  const nextDate = next instanceof Date ? next : next.toDate();
                  if (Number.isNaN(nextDate.getTime())) return;
                  setExpiryPreset('custom');
                  setExpiry(startOfDay(nextDate));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "filled",
                    error: expiryTooSoon,
                    helperText: expiryTooSoon
                      ? t('common:voucher.expiryBeforeIssued')
                      : t('common:voucher.expiryHint'),
                  },
                  popper: { sx: { zIndex: Z_INDEX.dialogPopover } },
                  dialog: { sx: { zIndex: Z_INDEX.dialogPopover } },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
            {EXPIRY_PRESETS.map((key) => (
              <Chip
                key={key}
                size="small"
                label={t(`common:voucher.expiryPresets.${key}`)}
                color={expiryPreset === key ? 'primary' : 'default'}
                variant={expiryPreset === key ? 'filled' : 'outlined'}
                onClick={() => applyExpiryPreset(key)}
              />
            ))}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 1.25,
            }}
          >
            <TextField variant="filled"
              type="number"
              label={t('common:voucher.quantity')}
              value={quantityLocked ? 1 : quantity}
              disabled={quantityLocked}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const next = Math.max(1, Math.min(MAX_VOUCHER_QUANTITY, parseInt(e.target.value, 10) || 1));
                setQuantity(next);
              }}
              helperText={
                quantityLocked
                  ? t('common:voucher.quantityLockedHint')
                  : t('common:voucher.quantityHint', { max: MAX_VOUCHER_QUANTITY })
              }
              inputProps={{ min: 1, max: MAX_VOUCHER_QUANTITY }}
            />
            <TextField variant="filled"
              label={t('common:voucher.code')}
              value={code}
              disabled={quantity > 1 && !quantityLocked}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              helperText={
                quantity > 1 && !quantityLocked
                  ? t('common:voucher.codeBatchHint')
                  : t('common:voucher.codeHint')
              }
            />
            <Autocomplete
              options={posOptions}
              getOptionLabel={(row) => row.pos_name || ''}
              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
              value={posLocked ? lockedPos : pos}
              disabled={posLocked}
              onChange={(_, row) => setPos(row)}
              loading={loadingLookups}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField variant="filled"
                  {...params}
                  label={t('common:voucher.pos')}
                  helperText={posLocked ? t('common:voucher.posLockedHint') : t('common:voucher.posHint')}
                />
              )}
            />
            <Autocomplete
              options={modeOptions}
              getOptionLabel={(row) => row.mode_name || ''}
              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
              value={mode}
              onChange={(_, row) => setMode(row)}
              loading={loadingLookups}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField
                  variant="filled"
                  {...params}
                  required={modeRequired}
                  label={t('common:tables.mode')}
                  helperText={modeRequired ? t('common:voucher.modeRequiredHint') : t('common:voucher.modeHint')}
                />
              )}
            />
          </Box>

          {voucher && (
            <Autocomplete
              options={[...VOUCHER_STATUSES]}
              getOptionLabel={(value) => {
                const label = t(voucherStatusI18nKey(value));
                return label.startsWith('common:') ? value : label;
              }}
              value={status}
              onChange={(_, value) => value && setStatus(value)}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
              renderInput={(params) => (
                <TextField variant="filled" {...params} label={t('common:voucher.status')} />
              )}
            />
          )}

          <TextField variant="filled"
            label={t('common:wallet.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common:ui.buttons.cancel')}</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSave}>
          {t(voucher ? 'common:ui.buttons.save' : 'common:ui.buttons.confirm')}
        </Button>
      </DialogActions>
    </MoleDialog>
  );
};

export default VoucherModal;
